import { defineStore } from 'pinia';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions';
import { waitForResumableUpload } from '~/lib/resumableUpload';
import { toMillis } from '~/lib/timestamps';
import type { MessageAttachment, UploadedFile } from '~/types/models';
import { CORE_DATA_MAX_AGE_MS, chunkRequestValues, runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { privateImmutableUploadMetadata } from '~/lib/storageUploadMetadata';
import { isImageUploadedFile } from '~/lib/uploadedFiles';

type FileEntityType = UploadedFile['entityType'];

function getFetchStatusCode(error: unknown): number | null {
    if (!error || typeof error !== 'object') {
        return null;
    }

    const fetchError = error as {
        status?: number;
        statusCode?: number;
        response?: { status?: number };
        data?: { statusCode?: number };
    };

    return fetchError.statusCode
        ?? fetchError.status
        ?? fetchError.response?.status
        ?? fetchError.data?.statusCode
        ?? null;
}

function isSkippablePortalFilesError(error: unknown): boolean {
    const statusCode = getFetchStatusCode(error);
    return statusCode === 403 || statusCode === 404;
}

export const useFilesStore = defineStore('files', {
    state: () => ({
        filesByEntity: {} as Record<string, UploadedFile[]>,
        loadingByEntity: {} as Record<string, boolean>,
        loadedAtByEntity: {} as Record<string, number>,
        filesError: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            const portalStore = useClientPortalStore();
            const tenantId = tenantStore.currentTenantId || portalStore.currentTenantId;
            if (!tenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantId;
        },

        _entityKey(entityType: FileEntityType, entityId: string): string {
            return `${entityType}:${entityId}`;
        },

        _shouldUsePortalFilesApi(): boolean {
            const route = useRoute();
            const path = String(route.path || '').trim();
            return path === '/portal' || path.startsWith('/portal/');
        },

        async _fetchPortalEntityFiles(tenantId: string, entityType: FileEntityType, entityId: string): Promise<UploadedFile[] | null> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const clientId = portalStore.currentClient?.id;

            if (!this._shouldUsePortalFilesApi()) {
                return null;
            }

            if (!clientId || !portalStore.currentTenantId) {
                return null;
            }

            if (!hasClientPortalPermission(portalStore.currentAccess, 'viewFiles')) {
                return [];
            }

            const token = await $auth.currentUser?.getIdToken();
            if (!token) {
                throw new Error('You must be signed in to load client portal files.');
            }

            const response = await $fetch<{ files: UploadedFile[] }>('/api/client-portal/files', {
                query: {
                    tenantId,
                    clientId,
                    entityType,
                    entityId,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            return Array.isArray(response.files) ? response.files : [];
        },

        async _fetchEntityFiles(tenantId: string, entityType: FileEntityType, entityId: string): Promise<UploadedFile[]> {
            const { $db } = useNuxtApp();
            const portalFiles = await this._fetchPortalEntityFiles(tenantId, entityType, entityId);
            if (portalFiles) {
                return portalFiles;
            }

            const filesCollection = collection($db, `tenants/${tenantId}/files`);
            const q = query(
                filesCollection,
                where('entityType', '==', entityType),
                where('entityId', '==', entityId),
            );
            const snap = await getDocs(q);

            return snap.docs.map((fileDoc) => ({
                id: fileDoc.id,
                ...fileDoc.data(),
            } as UploadedFile));
        },

        _sortFiles(files: UploadedFile[]): UploadedFile[] {
            return [...files].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        },

        _replaceCachedFile(entityType: FileEntityType, entityId: string, fileId: string, patch: Partial<UploadedFile>): void {
            const entityKey = this._entityKey(entityType, entityId);
            const existing = this.filesByEntity[entityKey] || [];
            this.filesByEntity[entityKey] = this._sortFiles(existing.map((file) => (
                file.id === fileId
                    ? { ...file, ...patch }
                    : file
            )));
        },

        sanitizeFilename(filename: string): string {
            return filename.replace(/[^a-zA-Z0-9.-]/g, '_');
        },

        async fetchFiles(entityType: FileEntityType, entityId: string, options: FreshRequestOptions = {}): Promise<UploadedFile[]> {
            const { $db } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const entityKey = this._entityKey(entityType, entityId);
            if (!options.force
                && Object.prototype.hasOwnProperty.call(this.filesByEntity, entityKey)
                && Date.now() - (this.loadedAtByEntity[entityKey] || 0) < (options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS)) {
                return this.filesByEntity[entityKey] || [];
            }
            this.loadingByEntity[entityKey] = true;

            try {
                const portalFiles = await this._fetchPortalEntityFiles(tenantId, entityType, entityId);
                if (portalFiles) {
                    this.filesByEntity[entityKey] = this._sortFiles(portalFiles);
                    this.loadedAtByEntity[entityKey] = Date.now();
                    return this.filesByEntity[entityKey] || [];
                }

                const filesCollection = collection($db, `tenants/${tenantId}/files`);
                const q = query(filesCollection, where('entityKey', '==', entityKey));
                const snap = await getDocs(q);

                const files = snap.docs.map((fileDoc) => ({
                    id: fileDoc.id,
                    ...fileDoc.data(),
                } as UploadedFile));

                this.filesByEntity[entityKey] = this._sortFiles(files);
                this.loadedAtByEntity[entityKey] = Date.now();
                return this.filesByEntity[entityKey] || [];
            } catch (error: any) {
                this.filesError = error.message || 'Failed to load files.';
                console.error('Error fetching files:', error);
                return [];
            } finally {
                this.loadingByEntity[entityKey] = false;
            }
        },

        async fetchFilesForEntities(
            entityType: FileEntityType,
            entityIds: string[],
            options: FreshRequestOptions = {},
        ): Promise<UploadedFile[]> {
            const normalizedEntityIds = Array.from(new Set(entityIds.map(id => String(id || '').trim()).filter(Boolean))).sort();
            if (normalizedEntityIds.length === 0) return [];

            if (this._shouldUsePortalFilesApi()) {
                const tenantId = this._getTenantId();
                const results = await Promise.all(normalizedEntityIds.map(entityId => this._fetchEntityFiles(tenantId, entityType, entityId)));
                return this._sortFiles(results.flat());
            }

            const tenantId = this._getTenantId();
            const entityKeys = normalizedEntityIds.map(entityId => this._entityKey(entityType, entityId));
            const hasData = entityKeys.every(entityKey => Object.prototype.hasOwnProperty.call(this.filesByEntity, entityKey));
            const maxAgeMs = options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS;
            if (!options.force && hasData && entityKeys.every(entityKey => (
                Date.now() - (this.loadedAtByEntity[entityKey] || 0) < maxAgeMs
            ))) {
                return this._sortFiles(entityKeys.flatMap(entityKey => this.filesByEntity[entityKey] || []));
            }

            await runFreshRequest(`files:${tenantId}:${entityType}:${normalizedEntityIds.join(',')}`, async () => {
                const { $db } = useNuxtApp();
                entityKeys.forEach((entityKey) => {
                    this.loadingByEntity[entityKey] = true;
                    this.filesByEntity[entityKey] = [];
                });

                try {
                    const snapshots = await Promise.all(chunkRequestValues(normalizedEntityIds).map((entityIdChunk) => (
                        getDocs(query(
                            collection($db, `tenants/${tenantId}/files`),
                            where('entityType', '==', entityType),
                            where('entityId', 'in', entityIdChunk),
                        ))
                    )));
                    for (const snapshot of snapshots) {
                        for (const fileDoc of snapshot.docs) {
                            const file = { id: fileDoc.id, ...fileDoc.data() } as UploadedFile;
                            const entityKey = this._entityKey(file.entityType, file.entityId);
                            this.filesByEntity[entityKey] = this._sortFiles([
                                ...(this.filesByEntity[entityKey] || []),
                                file,
                            ]);
                        }
                    }
                    entityKeys.forEach((entityKey) => {
                        this.loadedAtByEntity[entityKey] = Date.now();
                    });
                } finally {
                    entityKeys.forEach((entityKey) => {
                        this.loadingByEntity[entityKey] = false;
                    });
                }
            }, { ...options, maxAgeMs, hasData });

            return entityKeys.flatMap(entityKey => this.filesByEntity[entityKey] || []);
        },

        async fetchFilesForScope(entities: Array<{ entityType: FileEntityType, entityId: string }>): Promise<UploadedFile[]> {
            this.filesError = null;

            const tenantId = this._getTenantId();
            try {
                if (!this._shouldUsePortalFilesApi()) {
                    const idsByType = new Map<FileEntityType, string[]>();
                    for (const { entityType, entityId } of entities) {
                        idsByType.set(entityType, [...(idsByType.get(entityType) || []), entityId]);
                    }
                    const groupedResults = await Promise.all(Array.from(idsByType.entries()).map(([entityType, entityIds]) => (
                        this.fetchFilesForEntities(entityType, entityIds)
                    )));
                    return this._sortFiles(groupedResults.flat());
                }

                const results = await Promise.allSettled(
                    entities.map(({ entityType, entityId }) => this._fetchEntityFiles(tenantId, entityType, entityId)),
                );

                const failedResult = results.find((result) => result.status === 'rejected' && !isSkippablePortalFilesError(result.reason));
                if (failedResult?.status === 'rejected') {
                    throw failedResult.reason;
                }

                return this._sortFiles(results.flatMap((result) => (
                    result.status === 'fulfilled' ? result.value : []
                )));
            } catch (error: any) {
                this.filesError = error.message || 'Failed to load files.';
                console.error('Error fetching scoped files:', error);
                return [];
            }
        },

        async uploadFile(entityType: FileEntityType, entityId: string, file: File, options?: {
            onProgress?: (progress: number) => void;
        }): Promise<string> {
            const { $db, $storage } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const portalStore = useClientPortalStore();
            if (portalStore.currentClient?.id && !hasClientPortalPermission(portalStore.currentAccess, 'uploadFiles')) {
                throw new Error('You do not have permission to upload files.');
            }

            const tenantStore = useTenantStore();
            const entityKey = this._entityKey(entityType, entityId);

            const authStore = useAuthStore();
            const userStore = useUserStore();
            const userId = authStore.user?.uid || userStore.user?.id;
            if (!userId) {
                throw new Error('You must be logged in to upload files.');
            }

            const cleanFilename = this.sanitizeFilename(file.name);
            const storagePath = `tenants/${tenantId}/files/${entityType}/${entityId}/${Date.now()}-${cleanFilename}`;
            const fileRef = storageRef($storage, storagePath);

            await tenantStore.ensureStorageCapacity(tenantId, file.size);
            const uploadTask = uploadBytesResumable(fileRef, file, {
                ...privateImmutableUploadMetadata(file),
                customMetadata: { uploadedBy: userId },
            });
            await waitForResumableUpload(uploadTask, { onProgress: options?.onProgress });
            const fileURL = await getDownloadURL(fileRef);

            const payload = {
                tenantId,
                entityType,
                entityId,
                entityKey,
                fileName: file.name,
                fileURL,
                storagePath,
                contentType: file.type || 'application/octet-stream',
                sizeBytes: file.size,
                uploadedBy: userId,
                uploadedByName: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Unknown',
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            const fileDocRef = await addDoc(collection($db, `tenants/${tenantId}/files`), payload);
            const created = { id: fileDocRef.id, ...payload } as UploadedFile;

            const existing = this.filesByEntity[entityKey] || [];
            this.filesByEntity[entityKey] = this._sortFiles([created, ...existing]);
            await tenantStore.adjustStorageUsage(tenantId, file.size);

            return fileDocRef.id;
        },

        async linkMessageAttachmentToProject(source: MessageAttachment, clientId: string, projectId: string, sourceMessageId: string): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            if (!source.fileId || !source.storagePath || !source.fileURL) {
                throw new Error('Only stored message attachments can be added to Project files.');
            }

            const projectStore = useProjectStore();
            const project = projectStore.projects.find((entry) => entry.id === projectId)
                || await projectStore.fetchProjectById(projectId);
            if (!project || project.clientId !== clientId) {
                throw new Error('That Project does not belong to this Client.');
            }

            const entityKey = this._entityKey('project', projectId);
            if (!this.filesByEntity[entityKey]) await this.fetchFiles('project', projectId);
            const existingLink = (this.filesByEntity[entityKey] || []).find((file) => (
                file.storageOwnerFileId === source.fileId && file.sourceMessageId === sourceMessageId
            ));
            if (existingLink) return existingLink.id;

            const now = new Date();
            const payload: Omit<UploadedFile, 'id'> = {
                tenantId,
                entityType: 'project',
                entityId: projectId,
                entityKey,
                fileName: source.fileName,
                fileURL: source.fileURL,
                storagePath: source.storagePath,
                contentType: source.contentType,
                sizeBytes: source.sizeBytes,
                uploadedBy: useAuthStore().user?.uid || useUserStore().user?.id || 'message-attachment',
                uploadedByName: useAuthStore().user?.displayName || useUserStore().user?.displayName || 'Message attachment',
                createdAt: now,
                updatedAt: now,
                storageOwnerFileId: source.fileId,
                sourceMessageId,
            };
            const fileRef = await addDoc(collection($db, `tenants/${tenantId}/files`), payload);
            this.filesByEntity[entityKey] = this._sortFiles([
                { id: fileRef.id, ...payload },
                ...(this.filesByEntity[entityKey] || []),
            ]);
            return fileRef.id;
        },

        async updateFileMetadata(entityType: FileEntityType, entityId: string, fileId: string, patch: Partial<UploadedFile>): Promise<void> {
            const { $db } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const sanitizedPatch = Object.fromEntries(
                Object.entries(patch).filter(([, value]) => value !== undefined)
            ) as Partial<UploadedFile>;

            const nextPatch = {
                ...sanitizedPatch,
                updatedAt: new Date(),
            };

            await updateDoc(doc($db, `tenants/${tenantId}/files`, fileId), nextPatch);
            this._replaceCachedFile(entityType, entityId, fileId, nextPatch);
        },

        async assignClientFileToProject(file: UploadedFile, projectId: string): Promise<void> {
            if (file.entityType !== 'client') {
                throw new Error('Only files held at client level can be added to a project.');
            }
            if (!projectId) throw new Error('Choose a project first.');

            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const projectStore = useProjectStore();
            const project = projectStore.projects.find((entry) => entry.id === projectId)
                || await projectStore.fetchProjectById(projectId);
            if (!project) throw new Error('Project not found.');
            if (project.clientId !== file.entityId) {
                throw new Error('Choose a project linked to this client.');
            }

            const previousEntityKey = this._entityKey(file.entityType, file.entityId);
            const entityKey = this._entityKey('project', projectId);
            if (!this.filesByEntity[entityKey]) await this.fetchFiles('project', projectId);

            const isImage = isImageUploadedFile(file);
            const galleryCount = (this.filesByEntity[entityKey] || []).filter((entry) => entry.isGalleryItem).length;
            const nextPatch: Partial<UploadedFile> = {
                entityType: 'project',
                entityId: projectId,
                entityKey,
                ...(isImage ? {
                    isGalleryItem: true,
                    galleryVisibleInPortal: file.galleryVisibleInPortal !== false,
                    gallerySortOrder: galleryCount,
                } : {}),
                updatedAt: new Date(),
            };

            await updateDoc(doc($db, `tenants/${tenantId}/files`, file.id), nextPatch);
            this.filesByEntity[previousEntityKey] = (this.filesByEntity[previousEntityKey] || []).filter((entry) => entry.id !== file.id);
            this.filesByEntity[entityKey] = this._sortFiles([
                { ...file, ...nextPatch } as UploadedFile,
                ...(this.filesByEntity[entityKey] || []).filter((entry) => entry.id !== file.id),
            ]);
            this.loadedAtByEntity[previousEntityKey] = Date.now();
            this.loadedAtByEntity[entityKey] = Date.now();
        },

        async setProjectCover(projectId: string, fileId: string): Promise<void> {
            const { $db } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const projectStore = useProjectStore();
            const entityKey = this._entityKey('project', projectId);

            if (!this.filesByEntity[entityKey]) {
                await this.fetchFiles('project', projectId);
            }

            const files = this.filesByEntity[entityKey] || [];
            const target = files.find((file) => file.id === fileId);
            if (!target) {
                throw new Error('Gallery image not found.');
            }

            const now = new Date();
            const fileIdsToUpdate = new Set(
                files
                    .filter((file) => file.isProjectCover || file.id === fileId)
                    .map((file) => file.id),
            );

            await Promise.all(Array.from(fileIdsToUpdate).map((id) => {
                const payload = id === fileId
                    ? { isProjectCover: true, isGalleryItem: true, updatedAt: now }
                    : { isProjectCover: false, updatedAt: now };
                return updateDoc(doc($db, `tenants/${tenantId}/files`, id), payload);
            }));

            this.filesByEntity[entityKey] = this._sortFiles(files.map((file) => (
                fileIdsToUpdate.has(file.id)
                    ? {
                        ...file,
                        isProjectCover: file.id === fileId,
                        isGalleryItem: file.id === fileId ? true : file.isGalleryItem,
                        updatedAt: now,
                    }
                    : file
            )));

            await projectStore.updateProject(projectId, { profileImageURL: target.fileURL });
        },

        async clearProjectCover(projectId: string): Promise<void> {
            const { $db } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const projectStore = useProjectStore();
            const entityKey = this._entityKey('project', projectId);

            if (!this.filesByEntity[entityKey]) {
                await this.fetchFiles('project', projectId);
            }

            const files = this.filesByEntity[entityKey] || [];
            const coverFiles = files.filter((file) => file.isProjectCover);
            const now = new Date();

            await Promise.all(coverFiles.map((file) => updateDoc(doc($db, `tenants/${tenantId}/files`, file.id), {
                isProjectCover: false,
                updatedAt: now,
            })));

            this.filesByEntity[entityKey] = this._sortFiles(files.map((file) => ({
                ...file,
                isProjectCover: false,
                updatedAt: file.isProjectCover ? now : file.updatedAt,
            })));

            await projectStore.updateProject(projectId, { profileImageURL: '' });
        },

        async reorderGallery(projectId: string, orderedIds: string[]): Promise<void> {
            const { $db } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const entityKey = this._entityKey('project', projectId);

            if (!this.filesByEntity[entityKey]) {
                await this.fetchFiles('project', projectId);
            }

            const files = this.filesByEntity[entityKey] || [];
            const sortOrderById = new Map(orderedIds.map((id, index) => [id, index]));
            const now = new Date();

            await Promise.all(orderedIds.map((id, index) => updateDoc(doc($db, `tenants/${tenantId}/files`, id), {
                gallerySortOrder: index,
                updatedAt: now,
            })));

            this.filesByEntity[entityKey] = this._sortFiles(files.map((file) => (
                sortOrderById.has(file.id)
                    ? { ...file, gallerySortOrder: sortOrderById.get(file.id), updatedAt: now }
                    : file
            )));
        },

        async uploadGalleryImage(projectId: string, file: File, options?: {
            caption?: string;
            altText?: string;
            visibleInPortal?: boolean;
            setAsCover?: boolean;
        }): Promise<string> {
            if (!(file.type || '').startsWith('image/')) {
                throw new Error('Only image files can be added to the gallery.');
            }

            const projectStore = useProjectStore();
            const fileId = await this.uploadFile('project', projectId, file);
            const entityKey = this._entityKey('project', projectId);
            const files = this.filesByEntity[entityKey] || [];
            const galleryFiles = files.filter((item) => item.isGalleryItem);
            const shouldSetAsCover = options?.setAsCover !== false
                && galleryFiles.length === 0
                && !projectStore.currentProject?.profileImageURL;

            await this.updateFileMetadata('project', projectId, fileId, {
                isGalleryItem: true,
                galleryCaption: options?.caption?.trim() || '',
                galleryAltText: options?.altText?.trim() || '',
                galleryVisibleInPortal: options?.visibleInPortal !== false,
                gallerySortOrder: galleryFiles.length,
                isProjectCover: shouldSetAsCover,
            });

            const created = (this.filesByEntity[entityKey] || []).find((item) => item.id === fileId);
            if (shouldSetAsCover && created?.fileURL) {
                await projectStore.updateProject(projectId, { profileImageURL: created.fileURL });
            }

            return fileId;
        },

        async deleteFile(entityType: FileEntityType, entityId: string, file: UploadedFile): Promise<void> {
            const { $db, $storage } = useNuxtApp();
            this.filesError = null;

            const tenantId = this._getTenantId();
            const portalStore = useClientPortalStore();
            if (portalStore.currentClient?.id && !hasClientPortalPermission(portalStore.currentAccess, 'uploadFiles')) {
                throw new Error('You do not have permission to remove files.');
            }

            const tenantStore = useTenantStore();
            const entityKey = this._entityKey(entityType, entityId);

            await deleteDoc(doc($db, `tenants/${tenantId}/files`, file.id));

            const ownsStorageObject = !file.storageOwnerFileId;
            let storageDeleted = !file.storagePath && ownsStorageObject;
            if (file.storagePath && !file.storageOwnerFileId) {
                try {
                    await deleteObject(storageRef($storage, file.storagePath));
                } catch (storageError) {
                    storageDeleted = false;
                    console.warn('Failed to delete file from storage:', storageError);
                }
            }

            const existing = this.filesByEntity[entityKey] || [];
            this.filesByEntity[entityKey] = existing.filter((entry) => entry.id !== file.id);

            if (ownsStorageObject && storageDeleted && file.sizeBytes) {
                await tenantStore.adjustStorageUsage(tenantId, -Math.abs(Number(file.sizeBytes)));
            }
        },
    },
});
